import SEO from "../components/SEO";
import {
  MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon,
  ArrowRightIcon, ArrowUpRightIcon, ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

const channels = [
  {
    Icon: ChatBubbleLeftRightIcon,
    label: "Chat",
    title: "Chat ao vivo",
    value: "Resposta em < 5 min",
    dot: "bg-emerald-400",
  },
  {
    Icon: EnvelopeIcon,
    label: "E-mail",
    title: "pia.server@pco.co.ao",
    value: "Resposta em < 2h",
    dot: "bg-blue-light",
  },
  {
    Icon: PhoneIcon,
    label: "Telefone",
    title: "(+244) 944-969-865",
    value: "(+244) 990-969-865",
    dot: "bg-emerald-400",
  },
];

export default function Contact() {
  return (
    <>
      <SEO
        title="Contacto — Fale Connosco"
        description="Entre em contacto com a equipa PIA. Estamos em Benfica, Luanda, Angola. Telefone: (+244) 944-969-865. E-mail: pia.server@pco.co.ao. Suporte activo de Segunda a Sábado."
        path="/contact"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contacto — PIA Angola",
          "url": "https://pia-payment.vercel.app/contact",
          "description": "Contacte a equipa PIA em Luanda, Angola.",
          "breadcrumb": { "@type": "BreadcrumbList", "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://pia-payment.vercel.app/" },
            { "@type": "ListItem", "position": 2, "name": "Contacto", "item": "https://pia-payment.vercel.app/contact" }
          ]},
        }}
      />
      {/* ══════════════════════════════════════════
          HERO — editorial dark
      ══════════════════════════════════════════ */}
      <section className="relative min-h-[70vh] flex flex-col justify-center bg-[#020d18] overflow-hidden pt-24 pb-12">

        {/* Giant background word */}
        <span
          className="absolute right-0 top-1/2 -translate-y-1/2 font-extrabold leading-none select-none pointer-events-none"
          style={{
            fontSize: "20vw",
            color: "transparent",
            WebkitTextStroke: "1px rgba(43,171,227,0.07)",
            letterSpacing: "-0.04em",
          }}
        >
          OLÁ
        </span>

        {/* Glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(43,171,227,0.13) 0%, transparent 70%)" }} />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(5,105,163,0.18) 0%, transparent 70%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-8 w-full">
          {/* Label */}
          <div className="flex items-center gap-3 mb-10">
            <span className="w-8 h-px bg-blue-light" />
            <span className="text-blue-light text-xs font-semibold uppercase tracking-[0.2em]">Contacto</span>
          </div>

          <div className="flex items-end justify-between gap-12 max-md:flex-col max-md:items-start">

            {/* Headline */}
            <div className="flex-1 max-w-2xl">
              <h1
                className="font-extrabold leading-[1.0] mb-8 max-md:text-5xl"
                style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", letterSpacing: "-0.03em" }}
              >
                <span className="text-white">Fale</span><br />
                <span style={{ color: "transparent", WebkitTextStroke: "1.5px rgba(255,255,255,0.2)" }}>
                  connosco
                </span><br />
                <span className="text-transparent bg-clip-text"
                  style={{ backgroundImage: "linear-gradient(90deg, #2babe3 0%, #7dd3fc 60%)" }}>
                  hoje.
                </span>
              </h1>
              <p className="text-white/40 text-lg leading-relaxed max-w-md">
                A nossa equipa está pronta para responder a qualquer questão —
                seja sobre planos, integração ou suporte técnico.
              </p>
            </div>

            {/* Channel stack — right side */}
            <div className="flex flex-col gap-3 min-w-72 max-md:w-full">
              {channels.map(({ Icon, label, title, value, dot }) => (
                <div key={label}
                  className="flex items-center gap-4 border border-white/8 rounded-2xl px-5 py-4 hover:border-blue/30 hover:bg-white/3 transition-all group cursor-pointer">
                  <div className="w-9 h-9 rounded-xl bg-white/6 flex items-center justify-center shrink-0 group-hover:bg-blue/15 transition-colors">
                    <Icon className="w-4 h-4 text-white/50 group-hover:text-blue-light transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-sm font-semibold truncate">{title}</p>
                    <p className="text-white/30 text-xs mt-0.5">{value}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`w-1.5 h-1.5 rounded-full ${dot} animate-pulse`} />
                    <span className="text-[10px] text-white/30 uppercase tracking-widest font-semibold">{label}</span>
                  </div>
                </div>
              ))}

              {/* Status pill */}
              <div className="flex items-center justify-between border border-emerald-400/20 rounded-2xl px-5 py-3 bg-emerald-950/20">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-xs font-semibold">Equipa online agora</span>
                </div>
                <span className="text-white/25 text-[10px]">Seg–Sex 08–17h</span>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom fade to white */}
        <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, #f8fafc)" }} />
      </section>

      {/* ══════════════════════════════════════════
          FORM + INFO — split editorial
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-off-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-5 gap-14 max-md:grid-cols-1">

            {/* ── Form — 3 cols ── */}
            <div className="col-span-3 max-md:col-span-1">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-6 h-px bg-blue" />
                <span className="text-blue-light text-xs font-semibold uppercase tracking-widest">Mensagem directa</span>
              </div>
              <h2
                className="font-extrabold text-dark leading-tight mb-10 max-md:text-2xl"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", letterSpacing: "-0.02em" }}
              >
                Envie-nos<br />uma mensagem.
              </h2>

              <form className="flex flex-col gap-5" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-2 gap-5 max-sm:grid-cols-1">
                  {[
                    { label: "Nome completo", placeholder: "Ex: João Silva" },
                    { label: "Instituição",   placeholder: "Nome da sua escola" },
                  ].map(({ label, placeholder }) => (
                    <div key={label}>
                      <label className="block text-dark/50 text-[11px] font-bold uppercase tracking-widest mb-2">{label}</label>
                      <input type="text" placeholder={placeholder}
                        className="w-full border-0 border-b-2 border-border rounded-none bg-transparent px-0 py-3 text-sm text-dark placeholder:text-muted/40 outline-none focus:border-blue transition-colors" />
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-5 max-sm:grid-cols-1">
                  {[
                    { label: "E-mail",   placeholder: "joao@exemplo.com", type: "email" },
                    { label: "Telefone", placeholder: "+244 9XX XXX XXX", type: "text" },
                  ].map(({ label, placeholder, type }) => (
                    <div key={label}>
                      <label className="block text-dark/50 text-[11px] font-bold uppercase tracking-widest mb-2">{label}</label>
                      <input type={type} placeholder={placeholder}
                        className="w-full border-0 border-b-2 border-border rounded-none bg-transparent px-0 py-3 text-sm text-dark placeholder:text-muted/40 outline-none focus:border-blue transition-colors" />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-dark/50 text-[11px] font-bold uppercase tracking-widest mb-2">Assunto</label>
                  <select className="w-full border-0 border-b-2 border-border rounded-none bg-transparent px-0 py-3 text-sm text-dark outline-none focus:border-blue transition-colors appearance-none cursor-pointer">
                    <option value="">Seleccione um assunto</option>
                    <option>Informações sobre planos</option>
                    <option>Suporte técnico</option>
                    <option>Parceria institucional</option>
                    <option>Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-dark/50 text-[11px] font-bold uppercase tracking-widest mb-2">Mensagem</label>
                  <textarea rows={5} placeholder="Descreva como podemos ajudar..."
                    className="w-full border-0 border-b-2 border-border rounded-none bg-transparent px-0 py-3 text-sm text-dark placeholder:text-muted/40 outline-none focus:border-blue transition-colors resize-none" />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="group inline-flex items-center gap-3 font-bold px-8 py-4 rounded-full text-sm transition-all hover:scale-105 text-white"
                    style={{ background: "linear-gradient(90deg, #2babe3, #016fb9)" }}
                  >
                    Enviar mensagem
                    <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                      <ArrowRightIcon className="w-3 h-3" />
                    </span>
                  </button>
                </div>
              </form>
            </div>

            {/* ── Info — 2 cols ── */}
            <div className="col-span-2 flex flex-col gap-0 max-md:col-span-1">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-6 h-px bg-blue" />
                <span className="text-blue-light text-xs font-semibold uppercase tracking-widest">Onde estamos</span>
              </div>

              {/* Info rows */}
              {[
                { Icon: MapPinIcon,  label: "Endereço",                lines: ["Rua Principal, Benfica", "Luanda, Angola"] },
                { Icon: PhoneIcon,   label: "Telefone",                lines: ["(+244) 944-969-865", "(+244) 990-969-865"] },
                { Icon: EnvelopeIcon,label: "E-mail",                  lines: ["pia.server@pco.co.ao", "suporte@pia.co.ao"] },
                { Icon: ClockIcon,   label: "Horário de Atendimento",  lines: ["Seg – Sex: 08h00 – 17h00", "Sáb: 09h00 – 13h00"] },
              ].map(({ Icon, label, lines }, i, arr) => (
                <div key={label}
                  className={`flex gap-4 py-6 ${i < arr.length - 1 ? "border-b border-border" : ""}`}>
                  <div className="w-8 h-8 rounded-lg bg-[#020d18]/6 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-dark/50" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted/50 mb-1.5">{label}</p>
                    {lines.map((l) => (
                      <p key={l} className="text-dark text-sm font-semibold leading-relaxed">{l}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Dark company card */}
              <div
                className="mt-6 rounded-2xl p-6"
                style={{ background: "linear-gradient(145deg, #020d18, #002249)" }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center">
                    <BuildingOfficeIcon className="w-4 h-4 text-blue-light" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm leading-tight">PIA Platform</p>
                    <p className="text-white/30 text-[11px]">PCO Soluções Digitais · Angola</p>
                  </div>
                </div>

                <div className="space-y-2 mb-5">
                  {[
                    { val: "pia.server@pco.co.ao" },
                    { val: "Benfica, Luanda, Angola" },
                    { val: "(+244) 944-969-865" },
                  ].map(({ val }) => (
                    <p key={val} className="text-white/40 text-xs font-medium">{val}</p>
                  ))}
                </div>

                <a
                  href="https://maps.google.com/?q=Benfica,Luanda,Angola"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-blue-light text-xs font-semibold hover:text-white transition-colors"
                >
                  Ver no Google Maps <ArrowUpRightIcon className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          BOTTOM DARK STRIP — localização
      ══════════════════════════════════════════ */}
      <section className="bg-[#020d18] py-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

        <div className="relative max-w-6xl mx-auto px-6 flex items-center justify-between gap-8 max-md:flex-col max-md:text-center">
          <div>
            <p className="text-white/25 text-xs uppercase tracking-widest font-semibold mb-2">Localização</p>
            <h3 className="text-2xl font-extrabold text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
              Benfica, Luanda<br />
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}>
                Angola
              </span>
            </h3>
          </div>

          <div className="flex flex-col gap-2 max-md:items-center">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/40 text-xs">Activos de Segunda a Sábado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-light" />
              <span className="text-white/40 text-xs">Suporte digital 24/7 disponível</span>
            </div>
          </div>

          <a
            href="https://maps.google.com/?q=Benfica,Luanda,Angola"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 border border-white/12 hover:border-blue/40 rounded-full px-6 py-3.5 text-white/60 hover:text-white text-sm font-semibold transition-all"
          >
            Abrir no Google Maps
            <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </section>
    </>
  );
}
